/**
 * 离线同步队列启动器：在 App 启动时尝试 flush 一次同步队列。
 * 真实联网检测由 worker 提供的事件总线驱动；这里只做 best-effort 启动。
 */

import { useEffect } from 'react';
import { process as processSyncQueue } from '../lib/sync/queue';
import { client } from '../api/client';
import { apiPaths } from '../api/paths';
import type { AccountView } from '../api/types';
import { useAppDispatch, useAppState } from '../lib/state/store';

export const SyncBootstrap: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentAccount } = useAppState();
  const accountId = currentAccount?.id;
  useEffect(() => {
    if (currentAccount || typeof window === 'undefined' || !sessionStorage.getItem('fp_access')) return;
    let cancelled = false;
    void client.get<AccountView>(apiPaths.me.base).then((account) => {
      if (cancelled) return;
      dispatch({ type: 'setAccount', account: {
        id: account.id,
        displayName: account.display_name,
        maskedPhone: account.masked_phone,
        avatarUrl: account.avatar_url,
      } });
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [currentAccount, dispatch]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      void client.get<AccountView>(apiPaths.me.base).then((account) => {
        if (cancelled || account.id !== accountId) return;
        dispatch({ type: 'setAccount', account: {
          id: account.id, displayName: account.display_name,
          maskedPhone: account.masked_phone, avatarUrl: account.avatar_url,
        } });
      }).catch(() => undefined);
    };
    refresh();
    const timer = window.setInterval(refresh, 60 * 60 * 1000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [accountId, dispatch]);
  useEffect(() => {
    let cancelled = false;
    void processSyncQueue().catch(() => {
      // 网络层失败不影响 UI，本地保留，状态机下一次再试
    });
    const handleOnline = () => {
      if (!cancelled) {
        void processSyncQueue();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  return null;
};
